import {Injectable} from '@angular/core';

import {Subject} from "rxjs";

import {Map} from "leaflet";
import MiniMap from "leaflet-minimap";

import {MapConfigService} from "@commons/services/map/config/map-config.service";
import {MapService} from "@commons/services/map/map/map.service";

import {VehicleUIEventsService} from "@layout/vehicle/services/vehicle-uievents.service";

import {MapLayers, MapLayerType} from "@src/app/map/map-layers";

@Injectable({
  providedIn: 'root'
})
export class MinimapService {

  private readonly isMinimapVisibleLSKey = 'minimap-visible';
  private readonly miniMapLayers = new MapLayers();

  private miniMap?: any;
  private defaultZoomOffset = -3;
  private selectedVehicleZoomOffset = -6;
  private isVehicleSelected = false;
  private isRoutesTab = false;
  private map?: Map;

  private miniMapOptions = {
    position: 'bottomleft',
    zoomLevelOffset: this.defaultZoomOffset,
    toggleDisplay: true,
    minimized: false,
    width: 250,
    aimingRectOptions: {color: "red", weight: 2},
  };

  minimapVisibleChangedSub$ = new Subject<boolean>();

  get isMinimapVisible(): boolean {
    const item = localStorage.getItem(this.isMinimapVisibleLSKey);
    let result = true; //domyślnie minimapa jest widoczna o ile ktoś jej nie zminimalizuje
    if (item != null)
      result = item === "true";

    return result;
  }

  constructor(
    private vehicleUIEventsService: VehicleUIEventsService,
    private mapConfigService: MapConfigService,
    private mapService: MapService
  ) {
    this.minimapVisibleChangedSub$.subscribe(isVisible => {
      this.saveSettings(isVisible)
    });
  }

  zoomIn() {
    if (this.isVehicleSelected && !this.isRoutesTab)
      this.selectedVehicleZoomIn();
    else
      this.defaultZoomIn();
  }

  zoomOut() {
    if (this.isVehicleSelected && !this.isRoutesTab)
      this.selectedVehicleZoomOut();
    else
      this.defaultZoomOut();
  }

  onMapReady(map: Map) {
    this.map = map;
    this.subToIsRoutesTab();
    this.subToCurrentVehicleIdChange();

    this.recreateMinimap();
    this.mapService.mapLayerChanged.subscribe(mapLayerType => this.changeLayer(mapLayerType));
  }

  private changeLayer(mapLayerType: MapLayerType) {
    switch (mapLayerType) {
      case MapLayerType.emapa:
        this.miniMap!.changeLayer(this.miniMapLayers.emapaLayer);
        break;

      case MapLayerType.googleRoadmap:
        this.miniMap!.changeLayer(this.miniMapLayers.googleStreetsLayer);
        break;

      case MapLayerType.googleHybrid:
        this.miniMap!.changeLayer(this.miniMapLayers.googleHybridLayer);
        break;

      case MapLayerType.osm:
        this.miniMap!.changeLayer(this.miniMapLayers.osmLayer);
        break;
    }
  }

  private subToIsRoutesTab() {
    this.vehicleUIEventsService.isRoutesTabSelected$.subscribe(isRoutesTab => {
      this.isRoutesTab = isRoutesTab;
      this.recreateMinimap();
    });
  }

  private subToCurrentVehicleIdChange() {
    this.mapConfigService.currentVehicleIdChange().subscribe(change => {
      this.isVehicleSelected = change?.currentVehicleId > 0;
      this.recreateMinimap();
    });
  }

  private recreateMinimap() {
    //gdy minimapa jest zminimalizowana jej ponowne tworzenie powoduje jej pokazanie i zwinięcie dlatego jest to pomijane w tym przypadku
    if (this.miniMapOptions.minimized)
      return;

    this.miniMapOptions.zoomLevelOffset = this.isVehicleSelected && !this.isRoutesTab ? this.selectedVehicleZoomOffset : this.defaultZoomOffset;
    //kontrolka minimapy nie reaguje na zmianę wartości opcji ZoomLevelOffset i trzeba ją za każdym razem usuwać i
    // dodawać na nowo z nowym zoomem w zalezności od tego czy pojazd jest wybrany czy też nie
    if (this.miniMap) {
      this.map?.removeControl(this.miniMap);
    }
    this.miniMapOptions.minimized = !this.isMinimapVisible;
    this.miniMap = new MiniMap(this.miniMapLayers.osmLayer, this.miniMapOptions).addTo(this.map!);
    this.subToMinimapToggle();
  }

  private subToMinimapToggle() {
    this.miniMap.on("toggle", (data: any) => {
      this.miniMapOptions.minimized = !this.miniMapOptions.minimized;
      this.minimapVisibleChangedSub$.next(!data.minimized);
    });
  }

  private saveSettings(isVisible: boolean) {
    localStorage.setItem(this.isMinimapVisibleLSKey, isVisible.toString());
  }

  private selectedVehicleZoomIn() {
    if (this.selectedVehicleZoomOffset === 0)
      return;

    this.selectedVehicleZoomOffset += 1;
    this.recreateMinimap();
  }

  private selectedVehicleZoomOut() {
    if (this.selectedVehicleZoomOffset === -10)
      return;

    this.selectedVehicleZoomOffset -= 1;
    this.recreateMinimap();
  }

  private defaultZoomIn() {
    if (this.defaultZoomOffset === 0)
      return;

    this.defaultZoomOffset += 1;
    this.recreateMinimap();
  }

  private defaultZoomOut() {
    if (this.defaultZoomOffset === -10)
      return;

    this.defaultZoomOffset -= 1;
    this.recreateMinimap();
  }
}
